import React, { FC, useCallback, useMemo, useState } from 'react';
import { Input, Button, Select, Form, message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import BackButton from '../back_button/BackButton';
import {
	siteSurveyTemplatesRestCrudThunksForBuyer,
	siteSurveyTemplatesRestCrudThunksForSupplier,
} from '../../thunks/site_survey_templates_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { bulkCreateSiteSurveys } from '../../thunks/site_surveys_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import { safeEncodeURIComponent } from '../../utils/HistoryUtils';
import SelectWithAllToggle from '../common/SelectWithAllToggle';
import RegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import { useForm } from 'antd/lib/form/Form';

const defaultFormFieldStyle = { maxWidth: 640 };

const SiteSurveyAddLocationForm: FC<any> = ({
	history,
	redirectForwardUrl,
	formData,
	bulkCreate,
	locations,
	fetchSiteSurveyTemplates,
	fetchMultipleSiteSurveyTemplates,
	siteSurveyTemplates,
	userType,
	onSuccess,
}): React.ReactElement => {
	const [isLoading, setIsLoading] = useState(false);

	const [form] = useForm();

	const getPathFromSiteSurvey = useCallback(
		(ss) =>
			`/${userType}/siteSurveys/byTitle/${safeEncodeURIComponent(
				nullSafeGet('title', ss)
			)}/template/${safeEncodeURIComponent(nullSafeGet('siteSurveyTemplateId', ss))}/locations`,
		[userType]
	);

	const initialValues = useMemo(
		() => ({
			...(formData || {}),
		}),
		[formData]
	);

	const onFinish = (values) => {
		const entity = {
			...formData,
			...values,
			locationIds: nullSafeGet('newLocationIds.0', values)
				? [
						...nullSafeGetOrElse('locationIds', values, []),
						...nullSafeGetOrElse('newLocationIds', values, []),
				  ]
				: undefined,
		};

		setIsLoading(true);
		bulkCreate(entity)
			.then((records) => {
				const redirectTo = nullSafeGet('0', records)
					? getPathFromSiteSurvey(records[0])
					: redirectForwardUrl;
				if (redirectTo) {
					history.push(redirectTo);
				} else if (onSuccess) {
					onSuccess(records);
				}
			})
			.catch(message.error)
			.finally(() => setIsLoading(false));
	};

	const onSubmitClick = async () => {
		try {
			setIsLoading(true);
			await form.validateFields();
			form.submit();
		} catch {
			setIsLoading(false);
		}
	};

	return (
		<Form
			form={form}
			layout="vertical"
			className="supplierForm"
			style={defaultFormFieldStyle}
			initialValues={initialValues}
			onFinish={onFinish}
		>
			<Form.Item
				label="Name"
				name="title"
				validateTrigger="onBlur"
				rules={[
					{
						required: true,
						message: 'Please enter a title',
					},
				]}
			>
				<Input disabled />
			</Form.Item>
			<Form.Item label="Description" name="description">
				<Input.TextArea disabled />
			</Form.Item>
			<Form.Item
				label="Choose a Site Survey Template"
				name="siteSurveyTemplateId"
				rules={[
					{
						required: true,
						message: 'Please select a site survey template',
					},
				]}
			>
				<OWAsyncSelect
					stateSlice={siteSurveyTemplates}
					valueAccessor={(el) => el.id}
					disabled
					targetCollectionName="siteSurveyTemplatesIndex"
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchSiteSurveyTemplates(
							{ search: searchText },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleSiteSurveyTemplates(ids, targetCollectionName);
					}}
					renderRecord={(sst) => (
						<Select.Option key={sst.id} value={sst.id}>
							{sst.title}
						</Select.Option>
					)}
					sortBy={{
						sort_by: 'title',
						order: 'ascend',
					}}
				/>
			</Form.Item>

			<Form.Item name="locationIds" label="Existing Locations">
				<RegionsLocationsTreeSelect
					style={defaultFormFieldStyle}
					disabled
					mode="multiple"
					noIsActiveFilter
					filtersSlice={locations}
					targetCollectionName={'SITE_SURVEY_FORM_EXISTING_LOCATIONS_AUTOCOMPLETE'}
				/>
			</Form.Item>

			<SelectWithAllToggle
				label="Add Locations"
				name="newLocationIds"
				allName="allLocations"
				defaultFormFieldStyle={defaultFormFieldStyle}
				form={form}
				displaySelectAllWithLabel
				requiredIfNotAllSelected
			>
				<RegionsLocationsTreeSelect
					style={defaultFormFieldStyle}
					hiddenRecords={formData.locationIds}
					mode="multiple"
					noIsActiveFilter
					filtersSlice={locations}
					targetCollectionName={'SITE_SURVEY_FORM_LOCATIONS_AUTOCOMPLETE'}
				/>
			</SelectWithAllToggle>
			<Form.Item>
				<Button
					type="primary"
					loading={isLoading}
					className="contactForm__button"
					onClick={onSubmitClick}
				>
					Update site surveys
				</Button>
				<span style={{ marginLeft: '16px' }}>
					<BackButton buttonText="Cancel" />
				</span>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	locations: state.locations,
	siteSurveyTemplates: state.site_survey_templates,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	bulkCreate: (entity) => dispatch(bulkCreateSiteSurveys(ownProps.userType)(entity)),
	fetchSiteSurveyTemplates: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveyTemplatesRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: siteSurveyTemplatesRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleSiteSurveyTemplates: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? siteSurveyTemplatesRestCrudThunksForSupplier.read({ ids }, targetCollectionName)
				: siteSurveyTemplatesRestCrudThunksForBuyer.read({ ids }, targetCollectionName)
		),
});

export default withRouter(
	withUserType(
		connect(mapStateToProps, mapDispatchToProps)(SiteSurveyAddLocationForm as any) as any
	)
);
